<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
import dashboardIcons from 'shared/components/FluentIcon/dashboard-icons.json';

const props = defineProps({
  modelValue: {
    type: String,
    default: 'arrow-right',
  },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const isOpen = ref(false);

// List of icons available for selection, organized by category
const availableIcons = computed(() => [
  // Actions and Status
  { icon: 'arrow-right', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.ADVANCE') },
  { icon: 'checkmark', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.COMPLETED') },
  {
    icon: 'checkmark-circle',
    label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.APPROVED'),
  },
  { icon: 'add-circle', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.NEW') },
  {
    icon: 'star-emphasis',
    label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.HIGHLIGHT'),
  },
  { icon: 'heart', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.FAVORITE') },
  {
    icon: 'heart-handshake',
    label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.AGREEMENT'),
  },

  // People and Communication
  {
    icon: 'person-add',
    label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.PROSPECTING'),
  },
  { icon: 'people', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.TEAM') },
  { icon: 'chat', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.CONVERSATION') },
  { icon: 'mail-inbox', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.INBOX') },
  { icon: 'send', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.SEND') },
  {
    icon: 'megaphone',
    label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.ANNOUNCEMENT'),
  },

  // Documents and Organization
  { icon: 'document', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.DOCUMENT') },
  { icon: 'book', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.MANUAL') },
  {
    icon: 'book-copy',
    label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.DOCUMENTATION'),
  },
  { icon: 'folder', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.FOLDER') },
  { icon: 'filter', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.FILTER') },
  { icon: 'list', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.LIST') },

  // Business and Finance
  { icon: 'briefcase', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.BUSINESS') },
  {
    icon: 'credit-card-person',
    label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.PAYMENT'),
  },
  { icon: 'tag', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.TAG') },

  // Technology and Tools
  { icon: 'settings', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.SETTINGS') },
  { icon: 'code', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.DEVELOPMENT') },
  { icon: 'flash-on', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.TEST') },
  { icon: 'key', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.ACCESS') },
  { icon: 'globe', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.WEB') },

  // Time and Calendar
  { icon: 'calendar', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.SCHEDULE') },
  { icon: 'calendar-clock', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.TIME') },

  // Location and Navigation
  { icon: 'location', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.LOCATION') },
  { icon: 'home', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.HOME') },
  { icon: 'search', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.SEARCH') },

  // Visual and Media
  { icon: 'eye-show', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.VIEW') },
  { icon: 'image', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.IMAGE') },
  { icon: 'emoji', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.EMOJI') },

  // Indicators
  { icon: 'flame', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.URGENT') },
  {
    icon: 'arrow-trending-lines',
    label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.TREND'),
  },
  { icon: 'warning', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.WARNING') },
  { icon: 'info', label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.INFO') },
]);

const selectedIcon = computed(() => {
  return (
    availableIcons.value.find(i => i.icon === props.modelValue) || {
      icon: 'arrow-right',
      label: t('PIPELINE.STAGE_ICON_PICKER.ICONS.DEFAULT'),
    }
  );
});

const selectIcon = icon => {
  emit('update:modelValue', icon);
  isOpen.value = false;
};

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

// Fecha o dropdown quando clicar fora
const closeDropdown = () => {
  isOpen.value = false;
};
</script>

<template>
  <div class="relative" @click.stop>
    <!-- Botão de seleção -->
    <button
      type="button"
      class="w-full flex items-center gap-2 px-3 py-2 border border-n-weak rounded-lg text-sm bg-n-solid-2 hover:bg-n-alpha-1 transition-colors"
      @click="toggleDropdown"
    >
      <div
        class="w-8 h-8 rounded-lg bg-n-alpha-2 flex items-center justify-center"
      >
        <FluentIcon
          :icon="selectedIcon.icon"
          size="18"
          :icons="dashboardIcons"
          class="text-n-slate-11"
        />
      </div>
      <span class="flex-grow text-left text-n-slate-12">
        {{ selectedIcon.label }}
      </span>
      <FluentIcon
        :icon="isOpen ? 'chevron-up' : 'chevron-down'"
        size="16"
        :icons="dashboardIcons"
        class="text-n-slate-10"
      />
    </button>

    <!-- Dropdown de ícones -->
    <div
      v-if="isOpen"
      class="absolute z-50 mt-1 w-full bg-n-solid-2 border border-n-weak rounded-lg shadow-lg max-h-[280px] overflow-y-auto"
    >
      <div class="p-2">
        <div class="grid grid-cols-5 gap-1">
          <button
            v-for="item in availableIcons"
            :key="item.icon"
            type="button"
            class="flex flex-col items-center justify-center p-2 rounded-lg hover:bg-n-alpha-1 transition-colors"
            :class="{
              'bg-n-alpha-2 ring-2 ring-n-brand': modelValue === item.icon,
            }"
            :title="item.label"
            @click="selectIcon(item.icon)"
          >
            <FluentIcon
              :icon="item.icon"
              size="20"
              :icons="dashboardIcons"
              class="text-n-slate-11"
              :class="{
                'text-n-brand': modelValue === item.icon,
              }"
            />
          </button>
        </div>
      </div>
    </div>

    <!-- Overlay para fechar o dropdown -->
    <div v-if="isOpen" class="fixed inset-0 z-40" @click="closeDropdown" />
  </div>
</template>
